import { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import CrewMembersActions from "../../context/CrewMembersActionsContext";
import { UserRow } from "../../components/UsersTable";
import TableComponent from "../../components/TableComponent";
import { getMembersList } from "../../api/crew";
import { getComplianceWithCourses } from "../../api/compliance";
import { getRelationTree } from "../../api/courses";
import useCrew from "../../hooks/useCrew";
import { formatLastFirstName } from "../../services/formatters";
import { buildCatalogRequirements } from "../compliance/profileSetup";
import { withProfileSetupFlag, MemberWithProfileSetup } from "./withProfileSetupFlag";

interface MatchParams {
  crewId: string;
}

interface UserActions {
  edit: (userId: number) => void;
  roles: (userId: number) => void;
  properties: (userId: number) => void;
  positions: (userId: number) => void;
  operations: (userId: number) => void;
  aircraft: (userId: number) => void;
  allCertificates: (userId: number) => void;
}

type MembersProps = RouteComponentProps<MatchParams>;

const Members: FC<MembersProps> = ({ match, history }) => {
  const { t } = useTranslation();
  const crewIdParam = match?.params?.crewId;
  const crewId = Number(crewIdParam);
  const isValidCrewId = !isNaN(crewId) && crewId >= 1;

  const { crew } = useCrew();
  const [members, setMembers] = useState<MemberWithProfileSetup[] | null>(null);
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);

  useEffect(() => {
    if (!isValidCrewId) return;
    let cancelled = false;

    (async () => {
      // Fire all requests in parallel. The member list is rendered as soon as
      // it arrives (without waiting on the larger compliance batch); the
      // profile-setup marks are added when compliance resolves. The crew
      // catalog upgrades detection to per-role semantics (CREW-530); when it
      // fails, the legacy heuristic applies instead.
      const membersPromise = getMembersList(crewId, crew?.baseIds);
      // Cached batch ONLY — a fresh whole-crew calculation here recomputes
      // compliance for every member and is too expensive for a list visit.
      // Staleness is handled at the source instead: wherever properties change
      // (properties-edit), a single-user useCache=false calculation rewrites
      // that user's cache entry, which is exactly what this batch reads.
      const compliancePromise = getComplianceWithCourses(crewId, true, crew?.baseIds).catch((err) => {
        // The API owns its own error logs; a failed load just leaves the list
        // unmarked rather than blocking it.
        if (!cancelled) console.error("Failed to load compliance for member profile-setup flags", err);
        return null;
      });
      const catalogPromise = getRelationTree(crewId)
        .then((response) => buildCatalogRequirements(response.tree))
        .catch((err) => {
          if (!cancelled) console.error("Failed to load relation catalog for member profile-setup flags", err);
          return null;
        });

      const sorted = (await membersPromise).sort((a, b) =>
        formatLastFirstName(a?.fullname).localeCompare(formatLastFirstName(b?.fullname)),
      );
      if (cancelled) return;
      setMembers(withProfileSetupFlag(sorted));

      const [compliance, catalog] = await Promise.all([compliancePromise, catalogPromise]);
      if (cancelled || !compliance) return;
      setMembers(withProfileSetupFlag(sorted, compliance.data, catalog));
    })();

    return () => {
      cancelled = true;
    };
  }, [crewId, isValidCrewId, crew?.baseIds]);

  const baseUrl = `/crew/${crewId}/members`;

  // Create storage key matching the TableComponent's logic
  // This is used to store the forward navigation state used to restore the table state
  // when the user navigates back to the members list page.
  const getForwardNavKey = (): string => {
    const storageKey = "table." + match.path;
    return storageKey + ".forwardNav";
  };

  // Mark as forward navigation (not back button) so state won't be restored
  const markForwardNavigation = (): void => {
    sessionStorage.setItem(getForwardNavKey(), "true");
  };

  const nav = (userId: number, subpage: string): void => {
    markForwardNavigation();
    history.push(`${baseUrl}/${userId}/compliance/${subpage}`);
  };

  const userActions: UserActions = {
    edit(userId: number) {
      markForwardNavigation();
      history.push(`${baseUrl}/${userId}/edit`);
    },
    roles(userId: number) {
      // No-op: roles functionality not implemented in this component
    },
    properties(userId: number) {
      markForwardNavigation();
      history.push(`${baseUrl}/${userId}/edit/properties`);
    },
    positions(userId: number) {
      nav(userId, "positions");
    },
    operations(userId: number) {
      nav(userId, "operations");
    },
    aircraft(userId: number) {
      nav(userId, "aircraft");
    },
    allCertificates(userId: number) {
      markForwardNavigation();
      history.push(`/user/${userId}/certificates`);
    },
  };

  // The toggle appears only once compliance has flagged at least one member;
  // until then (or if compliance fails) the full list shows without marks.
  const anyIncomplete = members?.some((member) => member.profileSetupIncomplete) ?? false;
  const visibleMembers =
    members && showOnlyIncomplete && anyIncomplete
      ? members.filter((member) => member.profileSetupIncomplete)
      : members;

  if (!isValidCrewId) {
    return (
      <Typography variant="h6" color="error">
        {t("invalid_crew_id", { crewId: crewIdParam })}
      </Typography>
    );
  }

  return (
    <>
      <h2>{t("members_list")}</h2>

      {visibleMembers && (
        <CrewMembersActions.Provider value={userActions}>
          <TableComponent
            items={visibleMembers}
            renderChildren={(user: MemberWithProfileSetup) => <UserRow key={user.id} user={user} />}
            TopComponent={
              anyIncomplete ? (
                <FormControlLabel
                  control={
                    <Switch checked={showOnlyIncomplete} onChange={(e) => setShowOnlyIncomplete(e.target.checked)} />
                  }
                  label={t("show_only_incomplete_profiles")}
                />
              ) : undefined
            }
            header={[
              { title: t("table_headers.id"), sort: "id", style: { width: "2%" } },
              { title: t("table_headers.full_name"), sort: "fullname", style: { width: "19%" } },
              { title: t("table_headers.email"), sort: "email", style: { width: "19%" } },
              { title: t("table_headers.roles"), style: { width: "35%" } },
              { title: t("table_headers.properties"), style: { width: "20%" } },
              { title: t("table_headers.actions"), style: { width: "5%" } },
            ]}
            search={["username", "fullname", "email", "id", "roles"]}
            pagination
          />
        </CrewMembersActions.Provider>
      )}
    </>
  );
};

export default Members;
